import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/button";
import { Input } from "../components/input";
import { Heading } from "../components/heading";
import { Text } from "../components/text";
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "../components/table";
import { Dialog, DialogTitle } from "../components/dialog";
import { useUser } from "../components/UserContext";
import { useArchive } from "../contexts/DataContext";
import { ArrowLeftIcon, ArchiveBoxXMarkIcon } from "@heroicons/react/24/outline";
import { logBitacora } from "../utils/bitacora";

const ClientesArchivados = () => {
  const navigate = useNavigate();
  const { user, userData, getUserEmail } = useUser();
  const { fetchArchivedClients, unarchiveClient, fetchClientContracts } = useArchive();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(8);

  // Unarchive confirmation
  const [confirmDialog, setConfirmDialog] = useState({ open: false, client: null });
  const [unarchiving, setUnarchiving] = useState(false);

  // Expanded client to show contratos/fianzas
  const [expandedClientId, setExpandedClientId] = useState(null);
  const [expandedContracts, setExpandedContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(false);

  const loadClients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchArchivedClients();
      setClients(data);
    } catch (err) {
      console.error("Error fetching archived clients:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [fetchArchivedClients]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const handleUnarchive = async () => {
    const client = confirmDialog.client;
    if (!client) return;
    setUnarchiving(true);
    try {
      await unarchiveClient(client.id);

      try {
        await logBitacora({
          action: "UPDATE",
          collection: "clientes",
          documentId: client.id,
          changes: [{ field: "archived", oldValue: true, newValue: false }],
          metadata: {
            clientId: client.id,
            clientName: client.nombreEmpresa || "Sin nombre",
            description: `Desarchivó cliente "${client.nombreEmpresa || "Sin nombre"}"`,
          },
          user,
          userData,
        });
      } catch (logErr) {
        console.error("Failed to log unarchive to bitacora:", logErr);
      }

      setClients((prev) => prev.filter((c) => c.id !== client.id));
      if (expandedClientId === client.id) {
        setExpandedClientId(null);
        setExpandedContracts([]);
      }
    } catch (err) {
      console.error("Error unarchiving client:", err);
    } finally {
      setUnarchiving(false);
      setConfirmDialog({ open: false, client: null });
    }
  };

  const handleToggleExpand = async (client) => {
    if (expandedClientId === client.id) {
      setExpandedClientId(null);
      setExpandedContracts([]);
      return;
    }
    setExpandedClientId(client.id);
    setExpandedContracts([]);

    if (client.contracts && client.contracts.length > 0) {
      setLoadingContracts(true);
      try {
        const contracts = await fetchClientContracts(client.contracts);
        setExpandedContracts(contracts);
      } catch (err) {
        console.error("Error fetching contracts for archived client:", err);
      } finally {
        setLoadingContracts(false);
      }
    }
  };

  // Filter
  const filtered = clients.filter((c) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      (c.nombreEmpresa || "").toLowerCase().includes(s) ||
      (c.nombreContacto || "").toLowerCase().includes(s) ||
      (c.rfc || "").toLowerCase().includes(s)
    );
  });

  // Pagination
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginated = filtered.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  // Get fianzas from contracts
  const getFianzasFromContracts = (contracts) => {
    const fianzas = [];
    contracts.forEach((contrato) => {
      if (contrato.fianzas && contrato.fianzas.length > 0) {
        contrato.fianzas.forEach((fianza) => {
          fianzas.push({
            ...fianza,
            contratoTitle: contrato.tituloContrato?.value || "Sin título",
          });
        });
      }
    });
    return fianzas;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <Button outline color="surelia" onClick={() => navigate("/app/clientes")}>
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <Heading level={1} className="text-3xl font-bold text-azul-surelia mb-1">
              Clientes Archivados
            </Heading>
            <Text className="text-sm text-azul-surelia/70">
              {clients.length} cliente{clients.length !== 1 ? "s" : ""} archivado{clients.length !== 1 ? "s" : ""}
            </Text>
          </div>
        </div>
      </div>

      {/* Search + Items Per Page */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <Input
          type="text"
          placeholder="Buscar por empresa, RFC o contacto..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 sm:max-w-md w-full border-azul-surelia/30 focus:border-azul-surelia focus:ring-azul-surelia/20"
        />
        <div className="flex items-center gap-2 whitespace-nowrap">
          <label className="text-sm font-medium text-azul-surelia">Mostrar:</label>
          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="px-[calc(theme(spacing[3])-1px)] py-[calc(theme(spacing[1.5])-1px)] border border-azul-surelia/30 rounded-lg text-azul-surelia bg-white focus:border-azul-surelia focus:ring-2 focus:ring-azul-surelia/20 outline-none text-sm/6 min-w-[60px]"
          >
            <option value={5}>5</option>
            <option value={8}>8</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
          </select>
          <span className="text-sm text-azul-surelia/70">por página</span>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <Text className="text-red-700">Error al cargar clientes archivados. Intenta de nuevo.</Text>
        </div>
      )}

      {filtered.length > 0 ? (
        <div className="mt-6">
          <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHead className="bg-gradient-to-r from-zinc-600 to-zinc-500">
                  <TableRow>
                    <TableHeader className="text-white font-semibold py-4">Empresa</TableHeader>
                    <TableHeader className="text-white font-semibold py-4">RFC</TableHeader>
                    <TableHeader className="text-white font-semibold py-4">Contacto</TableHeader>
                    <TableHeader className="text-white font-semibold py-4">Correo</TableHeader>
                    <TableHeader className="text-white font-semibold py-4">Archivado por</TableHeader>
                    <TableHeader className="text-white font-semibold py-4">Acciones</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.map((cliente, index) => (
                    <React.Fragment key={cliente.id}>
                      <TableRow
                        onClick={() => handleToggleExpand(cliente)}
                        className={`cursor-pointer transition-all duration-200 hover:bg-zinc-50 ${
                          index % 2 === 0 ? "bg-white" : "bg-zinc-50/50"
                        } ${expandedClientId === cliente.id ? "bg-azul-surelia/5" : ""}`}
                      >
                        <TableCell className="font-semibold text-zinc-700 py-4">
                          {cliente.nombreEmpresa}
                        </TableCell>
                        <TableCell className="text-gray-700 py-4 font-mono text-sm">
                          {cliente.rfc || <span className="text-gray-400">--</span>}
                        </TableCell>
                        <TableCell className="text-gray-700 py-4">{cliente.nombreContacto}</TableCell>
                        <TableCell className="text-gray-600 text-sm py-4">{cliente.correoContacto}</TableCell>
                        <TableCell className="py-4">
                          <span className="inline-flex items-center px-3 py-1 rounded-full bg-zinc-100 text-zinc-600 text-xs font-medium">
                            {getUserEmail(cliente.archivedBy)}
                          </span>
                        </TableCell>
                        <TableCell className="py-4">
                          <Button
                            outline
                            color="surelia"
                            className="text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmDialog({ open: true, client: cliente });
                            }}
                          >
                            <ArchiveBoxXMarkIcon className="w-4 h-4 mr-1" />
                            Desarchivar
                          </Button>
                        </TableCell>
                      </TableRow>

                      {/* Expanded row: contratos & fianzas */}
                      {expandedClientId === cliente.id && (
                        <TableRow>
                          <TableCell colSpan={6} className="p-0">
                            <div className="bg-zinc-50 border-t border-b border-zinc-200 px-6 py-4">
                              {loadingContracts ? (
                                <div className="flex items-center justify-center py-6">
                                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-azul-surelia"></div>
                                </div>
                              ) : expandedContracts.length === 0 ? (
                                <Text className="text-zinc-500 text-sm text-center py-4">
                                  Este cliente no tiene contratos ni fianzas registrados.
                                </Text>
                              ) : (
                                <div className="space-y-4">
                                  {/* Contratos */}
                                  <div>
                                    <Heading level={4} className="text-sm font-semibold text-azul-surelia mb-2">
                                      Contratos ({expandedContracts.length})
                                    </Heading>
                                    <div className="grid gap-2">
                                      {expandedContracts.map((contrato) => (
                                        <div
                                          key={contrato.id}
                                          className="bg-white rounded-lg border border-zinc-200 px-4 py-3 flex items-center justify-between"
                                        >
                                          <div>
                                            <Text className="font-medium text-sm text-zinc-900">
                                              {contrato.tituloContrato?.value || "Sin título"}
                                            </Text>
                                            <Text className="text-xs text-zinc-500">
                                              {contrato.beneficiario?.value || contrato.contratante?.value || "N/A"} ·{" "}
                                              {new Intl.NumberFormat("es-MX", {
                                                style: "currency",
                                                currency: "MXN",
                                              }).format(contrato.montoContrato?.value || 0)}
                                            </Text>
                                          </div>
                                          <span
                                            className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                              contrato.estatus?.value === "activo"
                                                ? "bg-green-100 text-green-800"
                                                : contrato.estatus?.value === "completado"
                                                ? "bg-blue-100 text-blue-800"
                                                : "bg-gray-100 text-gray-800"
                                            }`}
                                          >
                                            {contrato.estatus?.value || "N/A"}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>

                                  {/* Fianzas */}
                                  {(() => {
                                    const fianzas = getFianzasFromContracts(expandedContracts);
                                    if (fianzas.length === 0) return null;
                                    return (
                                      <div>
                                        <Heading level={4} className="text-sm font-semibold text-azul-surelia mb-2">
                                          Fianzas ({fianzas.length})
                                        </Heading>
                                        <div className="grid gap-2">
                                          {fianzas.map((fianza, idx) => (
                                            <div
                                              key={idx}
                                              className="bg-white rounded-lg border border-zinc-200 px-4 py-3 flex items-center justify-between"
                                            >
                                              <div>
                                                <Text className="font-medium text-sm text-zinc-900">
                                                  {fianza.numeroFianza || "Sin número"} — {fianza.tipo || fianza.tipoFianza || "N/A"}
                                                </Text>
                                                <Text className="text-xs text-zinc-500">
                                                  Contrato: {fianza.contratoTitle}
                                                </Text>
                                              </div>
                                              <Text className="text-sm font-semibold text-verde-lima">
                                                {new Intl.NumberFormat("es-MX", {
                                                  style: "currency",
                                                  currency: "MXN",
                                                }).format(fianza.montoAfianzado || 0)}
                                              </Text>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-zinc-50/50 px-4 sm:px-6 py-4 border-t border-zinc-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="text-sm text-zinc-500 text-center sm:text-left">
                    Mostrando {startIndex + 1} - {Math.min(startIndex + itemsPerPage, filtered.length)} de{" "}
                    {filtered.length} clientes
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      outline
                      color="surelia"
                      className="text-sm px-3 py-2"
                    >
                      Anterior
                    </Button>
                    <span className="text-sm text-zinc-600">
                      {currentPage} / {totalPages}
                    </span>
                    <Button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      outline
                      color="surelia"
                      className="text-sm px-3 py-2"
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg border border-zinc-200 p-12">
          <div className="text-center">
            <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ArchiveBoxXMarkIcon className="w-8 h-8 text-zinc-400" />
            </div>
            <Text className="text-lg text-zinc-500">
              {searchTerm
                ? `No se encontraron clientes archivados que coincidan con "${searchTerm}".`
                : "No hay clientes archivados."}
            </Text>
          </div>
        </div>
      )}

      {/* Unarchive Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => !unarchiving && setConfirmDialog({ open: false, client: null })}
        size="sm"
      >
        <DialogTitle>Desarchivar Cliente</DialogTitle>
        <div className="mt-4">
          <Text>
            ¿Estás seguro de que deseas desarchivar a{" "}
            <strong>{confirmDialog.client?.nombreEmpresa}</strong>? El cliente y sus contratos/fianzas
            volverán a aparecer en las vistas principales.
          </Text>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button
            outline
            color="surelia"
            onClick={() => setConfirmDialog({ open: false, client: null })}
            disabled={unarchiving}
          >
            Cancelar
          </Button>
          <Button color="surelia" onClick={handleUnarchive} disabled={unarchiving}>
            {unarchiving ? "Desarchivando..." : "Desarchivar"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
};

export default ClientesArchivados;
